/**
 * «ДВОЙНАЯ ВЫГОДА» и «ПРО с зачётом» на Stripe-пути (решение владелицы
 * 30.10.2026): иностранец видит те же акции, что россиянин, только в своей
 * валюте. Право и итоговую сумму считает сервер (backend/server.py
 * _stripe_pro_offer, stripe_pro_offer.py) — Stripe списывает обычную цену
 * ПРО с купоном «на один раз».
 *
 * Здесь — подписи: цена первого месяца по акции (та же таблица, что
 * DOUBLE_BENEFIT_PRICES на бэкенде) и хук с суммой первого списания для этого
 * человека (GET /api/stripe/checkout-price — тот же расчёт, что у кассы).
 */
import { useEffect, useState } from 'react';
import { BACKEND_URL as API_URL } from '@/lib/backendUrl';
import { stripeDefaultCurrency } from '@/lib/visitorRegion';
import { formatStripeAmount } from '@/components/payment/StripePaymentModal';
import { CURRENCY_SYMBOLS } from '@/hooks/useCurrency';

/** Первый месяц ПРО по «ДВОЙНОЙ ВЫГОДЕ» — как backend DOUBLE_BENEFIT_PRICES. */
export const STRIPE_DOUBLE_BENEFIT_PRICES = { USD: 5.99, KZT: 2990 };

/** Акции Stripe, в которых есть зачёт разовой покупки (stripe_pro_offer.py). */
export const STRIPE_CREDIT_PROMOS = ['pro_credit_stripe', 'db_credit_stripe'];

export function stripeAmountLabel(amount, currency) {
  const cur = String(currency || '').toUpperCase();
  return formatStripeAmount(Number(amount), CURRENCY_SYMBOLS[cur] || cur);
}

/** «5,99 $» / «2 990 ₸» — или null, если для валюты цены акции нет. */
export function stripeDoubleBenefitLabel(currency = stripeDefaultCurrency()) {
  const cur = String(currency || '').toUpperCase();
  const amount = STRIPE_DOUBLE_BENEFIT_PRICES[cur];
  return amount ? stripeAmountLabel(amount, cur) : null;
}

// Один ответ на человека и валюту за загрузку страницы.
const cache = new Map();

export function resetStripeFirstMonthCache() {
  cache.clear();
}

function loadFirstMonth(token, currency) {
  const key = `${token}:${currency}`;
  if (!cache.has(key)) {
    const qs = new URLSearchParams({ plan: 'PRO_MONTHLY', currency });
    const p = fetch(`${API_URL}/api/stripe/checkout-price?${qs}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data && Number(data.amount) > 0) return data;
        throw new Error('no price');
      });
    p.catch(() => { if (cache.get(key) === p) cache.delete(key); });
    cache.set(key, p);
  }
  return cache.get(key);
}

/**
 * Сумма первого месяца ПРО через Stripe для вошедшего: { amount, regular,
 * promo, credit, currency } с готовыми подписями, или null — ещё нет / не нужно.
 */
export function useStripeFirstMonth(enabled, currency = stripeDefaultCurrency()) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let token = null;
    try { token = localStorage.getItem('auth_token'); } catch (_e) { token = null; }
    if (!enabled || !token || !currency) {
      setData(null);
      return undefined;
    }
    let cancelled = false;
    loadFirstMonth(token, currency)
      .then((d) => {
        if (cancelled) return;
        const cur = d.currency || currency;
        setData({
          amount: Number(d.amount),
          regular: Number(d.regular_amount) || null,
          promo: d.promo || null,
          credit: Number(d.pro_credit) || 0,
          currency: cur,
          amountLabel: stripeAmountLabel(d.amount, cur),
          regularLabel: Number(d.regular_amount) > 0 ? stripeAmountLabel(d.regular_amount, cur) : null,
          creditLabel: Number(d.pro_credit) > 0 ? stripeAmountLabel(d.pro_credit, cur) : null,
        });
      })
      .catch(() => { if (!cancelled) setData(null); });
    return () => { cancelled = true; };
  }, [enabled, currency]);
  return data;
}
